<template>
  <div class="deferred-video-player">
    <div v-if="!isActive" class="video-poster" @click="activateVideo">
      <picture v-if="posterSrc">
        <source
          v-for="source in posterSources"
          :key="`${source.type}-${source.srcset}`"
          :srcset="source.srcset"
          :type="source.type"
        >
        <img :src="posterSrc" :alt="alt" loading="lazy" decoding="async">
      </picture>
      <div v-else class="video-poster-placeholder"></div>
      <button type="button" class="video-play-button">{{ buttonText }}</button>
    </div>
    <video
      v-else
      ref="videoRef"
      :src="videoSrc"
      controls
      preload="metadata"
      playsinline
      class="video-element"
    ></video>
  </div>
</template>

<script setup>
import {defineProps, nextTick, onMounted, ref} from "vue"

const props = defineProps({
  src: {
    type: [String, Function],
    required: true,
  },
  poster: {
    type: [String, Function, Object],
    default: null,
  },
  alt: {
    type: String,
    default: "video poster",
  },
  buttonText: {
    type: String,
    default: "点击加载视频",
  },
})

const videoRef = ref(null)
const isActive = ref(false)
const videoSrc = ref("")
const posterSrc = ref("")
const posterSources = ref([])

const normalizeAsset = async (value) => {
  if (!value) {
    return null
  }

  if (typeof value === "function") {
    return normalizeAsset(await value())
  }

  return value
}

const normalizePoster = async (value) => {
  const asset = await normalizeAsset(value)

  if (!asset) {
    return {src: "", sources: []}
  }

  if (typeof asset === "string") {
    return {src: asset, sources: []}
  }

  return {
    src: asset.src || "",
    sources: Array.isArray(asset.sources) ? asset.sources.filter(Boolean) : [],
  }
}

const loadPoster = async () => {
  const poster = await normalizePoster(props.poster)
  posterSrc.value = poster.src
  posterSources.value = poster.sources
}

const activateVideo = async () => {
  if (isActive.value) {
    return
  }

  const src = await normalizeAsset(props.src)
  videoSrc.value = typeof src === "string" ? src : ""
  isActive.value = true

  await nextTick()

  if (videoRef.value && typeof videoRef.value.play === "function") {
    videoRef.value.play().catch(() => {})
  }
}

onMounted(() => {
  loadPoster()
})
</script>

<style lang="less" scoped>
.deferred-video-player {
  margin: 10px 0;
  border-radius: 6px;
  border: 1px solid #e7e9e8;
  overflow: hidden;
  background: #f7f8fa;
}

.video-poster {
  position: relative;
  cursor: pointer;
  background: #111827;
}

.video-poster picture,
.video-poster img,
.video-poster-placeholder,
.video-element {
  display: block;
  width: 100%;
  height: 400px;
  object-fit: cover;
}

.video-poster-placeholder {
  background: linear-gradient(135deg, #f3f4f6, #d1d5db);
}

.video-play-button {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  border: 0;
  border-radius: 999px;
  padding: 12px 20px;
  color: #fff;
  background: rgba(17, 24, 39, 0.78);
  font-size: 14px;
  cursor: pointer;
}
</style>
